// apps/roleforge-app/src/lib/unsent-message.ts
//
// Issue #1326 (1) — a message that never reached the server used to be
// destroyed. `ChatComposer` clears the textarea on send; on a stream failure
// `AuditConversePage` replaced local transcript state with the server's
// canonical `res.messages`, which by definition cannot contain the message the
// server never received. The owner's text then existed nowhere: not in the
// composer, not in the transcript, not on the server.
//
// A RoleForge audit is a multi-hour, multi-session interview. Losing a long
// answer to a wifi blip is not an inconvenience, it is losing the deliverable.
//
// ─── Why this is a LIST, not a slot ────────────────────────────────────────
//
// The first version of this fix stored one record per audit and overwrote it
// unconditionally. Adversarial review reproduced the obvious consequence:
// being offline is the single most likely way to get two failures in a row —
// the exact scenario #1326 describes — and the second failure silently
// destroyed the first. A four-paragraph answer was replaced by "hello?".
//
// Entries are appended and de-duplicated by exact text, so retrying a message
// that fails again refreshes its entry rather than stacking duplicates.

export type UnsentMessage = {
  /** The exact text the owner typed and we failed to deliver. */
  text: string;
  /** Customer-safe explanation of why it did not send. */
  reason: string;
  /** ISO timestamp, so the notice can say when it happened. */
  savedAt: string;
};

/** Bounded so a pathological offline loop cannot exhaust the storage quota. */
export const MAX_PINNED_UNSENT = 20;

function storageKey(scopeKey: string): string {
  return `roleforge:unsent-message:${scopeKey}`;
}

function storage(): Storage | undefined {
  return (globalThis as { sessionStorage?: Storage }).sessionStorage;
}

export function loadUnsentMessages(scopeKey: string): UnsentMessage[] {
  const store = storage();
  if (!store) return [];
  try {
    const parsed = JSON.parse(store.getItem(storageKey(scopeKey)) ?? 'null');
    // Tolerate the pre-list single-record shape so a tab that already pinned
    // something does not lose it across a deploy.
    if (isUnsentMessage(parsed)) return [parsed];
    return Array.isArray(parsed) ? parsed.filter(isUnsentMessage) : [];
  } catch {
    return [];
  }
}

export function saveUnsentMessages(scopeKey: string, entries: UnsentMessage[]): void {
  const store = storage();
  if (!store) return;
  try {
    if (entries.length === 0) store.removeItem(storageKey(scopeKey));
    else store.setItem(storageKey(scopeKey), JSON.stringify(entries.slice(-MAX_PINNED_UNSENT)));
  } catch {
    // Storage unavailable (private mode, quota). The in-memory copy still
    // renders — we lose reload survival, never the text itself.
  }
}

/**
 * Append `entry`, replacing any existing entry with identical text so a repeat
 * failure of the same message refreshes it rather than duplicating it.
 */
export function appendUnsentMessage(
  entries: UnsentMessage[],
  entry: UnsentMessage,
): UnsentMessage[] {
  return [...entries.filter((e) => e.text !== entry.text), entry].slice(-MAX_PINNED_UNSENT);
}

/** Remove the entry whose text matches exactly. */
export function removeUnsentMessage(
  entries: UnsentMessage[],
  text: string,
): UnsentMessage[] {
  return entries.filter((e) => e.text !== text);
}

export function isUnsentMessage(value: unknown): value is UnsentMessage {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.text === 'string'
    && v.text.length > 0
    && typeof v.reason === 'string'
    && typeof v.savedAt === 'string'
  );
}

export function createUnsentMessage(text: string, reason: string): UnsentMessage {
  return { text, reason, savedAt: new Date().toISOString() };
}
